import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronLeft,
  Receipt,
  X,
} from 'lucide-react-native';
import { useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { addDocument } from '@/lib/documents';
import { addExpense } from '@/lib/expenses';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { scanReceiptBase64 } from '@/lib/ocr';
import { getActiveProperty } from '@/lib/tasks';
import { guessExpenseCategory } from '@/lib/vendor-category';

interface ScannedRow {
  uri: string;
  status: 'pending' | 'ok' | 'failed';
  included: boolean;
  reason?: string;
  vendor: string | null;
  amount_cents: number | null;
  paid_on: number | null;
  category: string;
  title: string;
}

/**
 * Bulk receipt scanner — pick multiple photos at once, OCR each in
 * sequence (so we don't blast Vision in parallel and hit quotas), then
 * insert them as expenses. Failed scans show inline; the user can
 * uncheck any row to skip it before committing.
 */
export default function ImportPhotosScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const [rows, setRows] = useState<ScannedRow[]>([]);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);

  const pickAndScan = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      void notice({
        title: 'Photo access needed',
        message: 'Grant photo library access so we can read your receipts.',
        tone: 'warning',
      });
      return;
    }
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      base64: true,
      allowsMultipleSelection: true,
      selectionLimit: 20,
    });
    if (res.canceled || res.assets.length === 0) return;

    const initial: ScannedRow[] = res.assets.map((a) => ({
      uri: a.uri,
      status: 'pending',
      included: true,
      vendor: null,
      amount_cents: null,
      paid_on: null,
      category: 'Other',
      title: 'Receipt',
    }));
    setRows(initial);
    setScanning(true);

    try {
      for (let i = 0; i < res.assets.length; i++) {
        const asset = res.assets[i];
        const base64 = asset.base64 ?? null;
        if (!base64) {
          setRows((r) =>
            r.map((row, idx) =>
              idx === i ? { ...row, status: 'failed', included: false, reason: 'No image data' } : row
            )
          );
          continue;
        }
        const result = await scanReceiptBase64(base64);
        if (!result.ok) {
          const msg = result.error.kind === 'no-key'
            ? 'No Vision API key set'
            : result.error.kind === 'network'
            ? 'Network error'
            : result.error.message;
          setRows((r) =>
            r.map((row, idx) =>
              idx === i ? { ...row, status: 'failed', included: false, reason: msg } : row
            )
          );
          // No point trying the rest if the key is missing.
          if (result.error.kind === 'no-key') break;
          continue;
        }
        const { parsed } = result;
        const category = guessExpenseCategory(parsed.vendor) ?? 'Other';
        // Title is the business name only — date is its own column on the
        // expense, so duplicating it in the title adds noise.
        const title = parsed.vendor?.trim()
          || parsed.description?.trim()
          || 'Receipt';
        setRows((r) =>
          r.map((row, idx) =>
            idx === i
              ? {
                  ...row,
                  status: 'ok',
                  vendor: parsed.vendor,
                  amount_cents: parsed.amount_cents,
                  paid_on: parsed.date,
                  category,
                  title,
                  // Auto-deselect rows with no amount — nothing useful to import.
                  included: parsed.amount_cents != null && parsed.amount_cents > 0,
                  reason:
                    parsed.amount_cents == null
                      ? 'No total found — edit later or skip'
                      : undefined,
                }
              : row
          )
        );
      }
    } finally {
      setScanning(false);
    }
  };

  const toggle = (i: number) => {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, included: !row.included } : row)));
  };

  const doImport = async () => {
    const valid = rows.filter((r) => r.included && r.status === 'ok' && r.amount_cents != null);
    if (valid.length === 0) return;
    setImporting(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      // Step 1: persist each receipt image into app storage so the URI
      // survives launches, BEFORE we open the SQLite transaction.
      // persistPickedFile is async + slow (file copy) and shouldn't run
      // inside withTransactionAsync — that would lock the DB during disk
      // I/O. We collect the persisted URI per row, then write
      // documents + expenses inside the transaction in one fast pass.
      const persisted: { row: ScannedRow; fileUri: string }[] = [];
      for (const r of valid) {
        const ext = r.uri.match(/\.[a-zA-Z]+$/)?.[0] ?? '.jpg';
        const fileName = `receipt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
        const fileUri = await persistPickedFile(r.uri, fileName);
        persisted.push({ row: r, fileUri });
      }
      await db.withTransactionAsync(async () => {
        for (const { row: r, fileUri } of persisted) {
          // Save the receipt as a Document row first, then attach it
          // to the expense via receipt_doc_id. Same pattern inventory
          // uses — viewable in /media, deletable, sharing-aware.
          const docId = await addDocument(db, {
            propertyId: prop.id,
            kind: 'receipt',
            title: r.vendor
              ? `Receipt — ${r.vendor}`
              : `Receipt — ${new Date(r.paid_on ?? Date.now()).toLocaleDateString()}`,
            file_uri: fileUri,
            mime_type: 'image/jpeg',
            vendor: r.vendor,
            cost_cents: r.amount_cents,
          });
          await addExpense(db, {
            propertyId: prop.id,
            category: r.category,
            vendor: r.vendor,
            amount_cents: r.amount_cents!,
            paid_on: r.paid_on ?? Date.now(),
            description: r.title,
            recurring: null,
            tax_deductible: 0,
            receipt_doc_id: docId,
          });
        }
      });
      await notice({
        title: 'Receipts imported',
        message: `Added ${valid.length} expense${valid.length === 1 ? '' : 's'} from photos.`,
        tone: 'info',
      });
      router.replace('/expenses' as never);
    } catch (err) {
      void notice({ title: 'Import failed', message: String(err), tone: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const okCount = rows.filter((r) => r.status === 'ok').length;
  const includedCount = rows.filter((r) => r.included && r.status === 'ok' && r.amount_cents != null).length;
  const failCount = rows.filter((r) => r.status === 'failed').length;
  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/expenses')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Scan receipts</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Import from photos</Text>
        <Text style={styles.sub}>
          Pick a stack of receipt photos at once. We&apos;ll read each one,
          guess the vendor, amount, date, and category — you uncheck
          anything that&apos;s off before importing.
        </Text>

        <Pressable
          onPress={pickAndScan}
          disabled={scanning}
          style={({ pressed }) => [
            styles.pickBtn,
            pressed && { transform: [{ scale: 0.98 }] },
            scanning && { opacity: 0.6 },
          ]}>
          <Camera size={18} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.pickText}>
            {scanning
              ? `Scanning… ${okCount + failCount} of ${rows.length}`
              : rows.length > 0
              ? 'Pick a different stack'
              : 'Pick photos'}
          </Text>
        </Pressable>

        {rows.length > 0 ? (
          <View style={styles.summary}>
            <Text style={styles.summaryText}>
              <Text style={styles.summaryBold}>{includedCount}</Text> ready to import
              {failCount > 0 ? (
                <>
                  {' · '}
                  <Text style={[styles.summaryBold, { color: Palette.clay }]}>
                    {failCount} failed
                  </Text>
                </>
              ) : null}
              {pendingCount > 0 ? ` · ${pendingCount} scanning…` : ''}
            </Text>
          </View>
        ) : null}

        <View style={{ marginTop: 8 }}>
          {rows.map((r, i) => (
            <View key={`${r.uri}-${i}`} style={styles.row}>
              <Image source={{ uri: r.uri }} style={styles.thumb} />
              <View style={{ flex: 1, minWidth: 0 }}>
                {r.status === 'pending' ? (
                  <Text style={styles.rowMeta}>Scanning…</Text>
                ) : r.status === 'failed' ? (
                  <>
                    <Text style={styles.rowTitle} numberOfLines={1}>Couldn&apos;t scan</Text>
                    <Text style={styles.rowMeta} numberOfLines={2}>{r.reason}</Text>
                  </>
                ) : (
                  <>
                    <Text style={styles.rowTitle} numberOfLines={1}>{r.title}</Text>
                    <Text style={styles.rowMeta} numberOfLines={1}>
                      {[
                        r.amount_cents != null
                          ? `$${(r.amount_cents / 100).toLocaleString()}`
                          : 'no total',
                        r.category,
                        r.paid_on
                          ? new Date(r.paid_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : null,
                      ].filter(Boolean).join(' · ')}
                    </Text>
                    {r.reason ? <Text style={styles.rowWarn}>{r.reason}</Text> : null}
                  </>
                )}
              </View>
              {r.status === 'ok' ? (
                <Pressable
                  onPress={() => toggle(i)}
                  hitSlop={8}
                  style={({ pressed }) => [
                    styles.check,
                    r.included && styles.checkOn,
                    pressed && { opacity: 0.7 },
                  ]}>
                  {r.included ? (
                    <Check size={14} color={Palette.cream} strokeWidth={2.4} />
                  ) : null}
                </Pressable>
              ) : r.status === 'failed' ? (
                <View style={styles.checkFail}>
                  <X size={12} color={Palette.clay} strokeWidth={2.4} />
                </View>
              ) : (
                <View style={[styles.check, { opacity: 0.4 }]} />
              )}
            </View>
          ))}
        </View>

        {includedCount > 0 ? (
          <Pressable
            onPress={doImport}
            disabled={importing || scanning}
            style={({ pressed }) => [
              styles.importBtn,
              (importing || scanning) && { opacity: 0.5 },
              pressed && { transform: [{ scale: 0.98 }] },
            ]}>
            <Receipt size={18} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.importText}>
              {importing
                ? 'Importing…'
                : `Import ${includedCount} expense${includedCount === 1 ? '' : 's'}`}
            </Text>
          </Pressable>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  pickBtn: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: '#EAF0EA',
    borderWidth: 1.5, borderStyle: 'dashed', borderColor: Palette.forest,
    paddingVertical: 16, borderRadius: 14,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.forest },
  summary: { marginTop: Spacing.three, marginHorizontal: Spacing.four },
  summaryText: { fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft },
  summaryBold: { fontFamily: Fonts.sansBold, color: Palette.forest },
  row: {
    marginTop: 8, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg,
    flexDirection: 'row', alignItems: 'center', gap: 10,
    padding: 10,
  },
  thumb: {
    width: 48, height: 48, borderRadius: 10,
    backgroundColor: Palette.sandDeep,
  },
  rowTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  rowMeta: {
    marginTop: 2, fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  rowWarn: {
    marginTop: 2, fontFamily: Fonts.sansBold, fontSize: 11.5, color: Palette.honeyDeep,
  },
  check: {
    width: 24, height: 24, borderRadius: 12,
    borderWidth: 1.5, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.sand,
  },
  checkOn: {
    backgroundColor: Palette.forest, borderColor: Palette.forest,
  },
  checkFail: {
    width: 24, height: 24, borderRadius: 12,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(176,95,60,0.15)',
  },
  importBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  importText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
});
